interface VoiceServiceConfig {
  language: string;
  onTranscript?: (text: string) => void;
  onError?: (error: Error) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

class VoiceService {
  private recognition: any = null;
  private synthesis: SpeechSynthesis | null = null;
  private isListening = false;
  private isSpeaking = false;
  private config: VoiceServiceConfig;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voiceEnabled = true;
  private silenceTimer: any = null;
  private lastSpeechTime = 0;
  private SILENCE_TIMEOUT = 10000; // 10 seconds of silence triggers auto-stop
  private hasReceivedSpeech = false;

  constructor(config: VoiceServiceConfig) {
    this.config = config;
    this.initializeSpeechRecognition();
    this.initializeSpeechSynthesis();
  }

  private initializeSpeechRecognition() {
    if (typeof window === 'undefined') return;

    const SpeechRecognition = (window as any).SpeechRecognition || 
                              (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true; // Changed to true for silence detection
    this.recognition.interimResults = true;
    this.recognition.maxAlternatives = 1;
    
    this.updateLanguage(this.config.language);

    this.recognition.onstart = () => {
      this.isListening = true;
      this.hasReceivedSpeech = false;
      this.finalTranscript = '';
      this.lastSpeechTime = Date.now();
      this.startSilenceDetection();
      this.config.onStart?.();
    };

    this.recognition.onresult = (event: any) => {
      const last = event.results.length - 1;
      const transcript = event.results[last][0].transcript;
      const isFinal = event.results[last].isFinal;
      
      // Update last speech time when we receive any speech
      this.lastSpeechTime = Date.now();
      this.hasReceivedSpeech = true;
      
      // Reset silence timer on new speech
      this.resetSilenceTimer();
      
      if (isFinal) {
        this.config.onTranscript?.(transcript);
      }
    };

    this.recognition.onspeechend = () => {
      // Speech has ended, start countdown for auto-stop
      this.startSilenceTimer();
    };

    this.recognition.onerror = (event: any) => {
      this.isListening = false;
      this.clearSilenceDetection();
      console.error('Speech recognition error:', event.error);
      
      if (event.error === 'not-allowed') {
        this.config.onError?.(new Error('Microphone access denied. Please allow microphone access to use voice input.'));
      } else if (event.error !== 'aborted') {
        this.config.onError?.(new Error(`Speech recognition error: ${event.error}`));
      }
    };

    this.recognition.onend = () => {
      this.isListening = false;
      this.clearSilenceDetection();
      
      this.config.onEnd?.();
    };
  }

  private initializeSpeechSynthesis() {
    if (typeof window === 'undefined') return;
    
    if ('speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
    } else {
      console.warn('Speech synthesis not supported in this browser');
    }
  }

  private startSilenceDetection() {
    // Check for silence every 500ms
    const checkInterval = setInterval(() => {
      if (!this.isListening) {
        clearInterval(checkInterval);
        return;
      }
      
      const timeSinceLastSpeech = Date.now() - this.lastSpeechTime;
      
      // If we've had speech and now silence for more than threshold
      if (this.hasReceivedSpeech && timeSinceLastSpeech > this.SILENCE_TIMEOUT) {
        console.log('Auto-stopping due to silence');
        this.stopListening();
        clearInterval(checkInterval);
      }
    }, 500);
  }

  private startSilenceTimer() {
    this.clearSilenceTimer();
    
    this.silenceTimer = setTimeout(() => {
      if (this.isListening && this.hasReceivedSpeech) {
        console.log('Auto-stopping due to silence after speech');
        this.stopListening();
      }
    }, this.SILENCE_TIMEOUT);
  }

  private resetSilenceTimer() {
    this.clearSilenceTimer();
    // Restart the timer
    this.startSilenceTimer();
  }

  private clearSilenceTimer() {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private clearSilenceDetection() {
    this.clearSilenceTimer();
    this.hasReceivedSpeech = false;
  }

  private getLanguageCode(language: string): string {
    const languageMap: Record<string, string> = {
      'en': 'en-US',
      'zh-TW': 'zh-HK',
      'zh-CN': 'zh-CN',
      'da': 'da-DK',
      'de': 'de-DE',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'it': 'it-IT',
      'nl': 'nl-NL',
      'pt': 'pt-PT',
      'ja': 'ja-JP',
      'ko': 'ko-KR',
      'th': 'th-TH'
    };
    
    return languageMap[language] || 'en-US';
  }

  updateLanguage(language: string) {
    this.config.language = language;
    if (this.recognition) {
      this.recognition.lang = this.getLanguageCode(language);
    }
  }

  startListening(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.recognition) {
        reject(new Error('Speech recognition not supported'));
        return;
      }

      if (this.isListening) {
        reject(new Error('Already listening'));
        return;
      }

      if (this.isSpeaking) {
        this.stopSpeaking();
      }

      try {
        this.recognition.start();
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  stopListening() {
    if (this.recognition && this.isListening) {
      this.clearSilenceDetection();
      this.recognition.stop();
    }
  }

  speak(text: string, options?: { rate?: number; pitch?: number; volume?: number }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.synthesis || !this.voiceEnabled) {
        resolve();
        return;
      }

      if (this.isSpeaking) {
        this.stopSpeaking();
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = this.getLanguageCode(this.config.language);
      utterance.rate = options?.rate || 1;
      utterance.pitch = options?.pitch || 1;
      utterance.volume = options?.volume || 1;

      const voices = this.synthesis.getVoices();
      const langCode = this.getLanguageCode(this.config.language);
      const voice = voices.find(v => v.lang === langCode) || voices[0];
      if (voice) {
        utterance.voice = voice;
      }

      utterance.onstart = () => {
        this.isSpeaking = true;
        this.currentUtterance = utterance;
      };

      utterance.onend = () => {
        this.isSpeaking = false;
        this.currentUtterance = null;
        resolve();
      };

      utterance.onerror = (event) => {
        this.isSpeaking = false;
        this.currentUtterance = null;
        console.error('Speech synthesis error:', event);
        reject(new Error('Speech synthesis failed'));
      };

      this.synthesis.speak(utterance);
    });
  }

  stopSpeaking() {
    if (this.synthesis && this.isSpeaking) {
      this.synthesis.cancel();
      this.isSpeaking = false;
      this.currentUtterance = null;
    }
  }

  toggleVoice(enabled: boolean) {
    this.voiceEnabled = enabled;
    if (!enabled) {
      this.stopSpeaking();
    }
  }

  setSilenceTimeout(milliseconds: number) {
    this.SILENCE_TIMEOUT = milliseconds;
  }

  getIsListening(): boolean {
    return this.isListening;
  }

  getIsSpeaking(): boolean {
    return this.isSpeaking;
  }

  isVoiceEnabled(): boolean {
    return this.voiceEnabled;
  }

  isSupported(): boolean {
    const hasSpeechRecognition = !!(window as any).SpeechRecognition || 
                                 !!(window as any).webkitSpeechRecognition;
    const hasSpeechSynthesis = 'speechSynthesis' in window;
    return hasSpeechRecognition && hasSpeechSynthesis;
  }

  destroy() {
    this.stopListening();
    this.stopSpeaking();
    this.clearSilenceDetection();
    this.recognition = null;
    this.synthesis = null;
  }
}

export default VoiceService;